import React, { useState, useEffect } from 'react';
import { Star, Clock, Users, ChevronRight, X, Edit, Trash2, PlayCircle, CheckCircle, XCircle, User } from 'lucide-react';
import AppIcon from './icons/AppIcon';
import '../styles/ChallengeCard.css';
import { getChallengeParticipants } from '../utils/challengesApi';

// ==================== CHALLENGE CARD - MODERN DESIGN ====================
export default function ChallengeCard({ 
  challenge, 
  onClick, 
  onParticipate, 
  onCancelParticipation,
  isAdmin = false,
  onEdit,
  onDelete,
  userParticipation = null,
  isPermanentlyCancelled = false
}) {
  // Default challenge data
  const {
    title = 'Win 3 tennis matches',
    description = 'Complete challenges to earn XP',
    rewardXP = 50,
    targetValue = 3,
    metricType = 'matchs',
    endDate = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    difficulty = 'easy',
    userProgress = { progress: 0, currentValue: 0 },
    icon = 'activity',
    participantCount = 0,
    _isPlaceholder = false
  } = challenge || {};

  const displayIcon = {
    target: 'target',
    trophy: 'trophy',
    star: 'star',
    fire: 'flame',
    football: 'match',
    basketball: 'activity',
    tennis: 'activity',
    running: 'activity'
  }[String(icon).toLowerCase()] || icon;

  // State for participants
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  // Fetch participants when challenge is loaded
  useEffect(() => {
    const fetchParticipants = async () => {
      // Only fetch participants if challenge has a valid ID
      if (challenge?._id && typeof challenge._id === 'string' && !challenge._id.startsWith('ph-')) {
        setLoadingParticipants(true);
        try {
          const result = await getChallengeParticipants(challenge._id);
          if (result.success && result.participants) {
            setParticipants(result.participants);
          }
        } catch (err) {
          console.error('Error fetching participants:', err);
        } finally {
          setLoadingParticipants(false);
        }
      }
    };

    fetchParticipants();
  }, [challenge?._id]);

  // Calculate progress
  const progress = userProgress?.progress || 0;
  const currentValue = userProgress?.currentValue || 0;
  const isComplete = progress >= 100;
  
  // Check participation status
  const userStatus = userProgress?.userStatus || 'idle';
  const isParticipating = userStatus === 'active' || userStatus === 'pending_review';
  const isCancelledFromStatus = userStatus === 'cancelled';
  // Use prop isPermanentlyCancelled (from parent) OR local status
  const isCancelled = isPermanentlyCancelled || isCancelledFromStatus;
  const isCompleted = userStatus === 'completed' || isComplete;

  // Format remaining time
  const formatRemaining = (endDate) => {
    const end = new Date(endDate).getTime();
    const remaining = Math.max(end - Date.now(), 0);
    const s = Math.floor(remaining / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    
    if (d > 0) return `${d}j ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return 'Expiré';
  };

  // Difficulty config
  const difficultyConfig = {
    easy: { label: 'Facile', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
    medium: { label: 'Moyen', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
    hard: { label: 'Difficile', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' }
  };
  const diffConfig = difficultyConfig[difficulty] || difficultyConfig.easy;

  // Duration label
  const durationLabels = {
    daily: 'Quotidien',
    weekly: 'Hebdomadaire',
    monthly: 'Mensuel',
    seasonal: 'Saisonnier',
    special: 'Spécial'
  };
  const durationLabel = durationLabels[challenge?.duration] || challenge?.duration || 'Quotidien';

  // Handle participate button click
  const handleParticipate = (e) => {
    e.stopPropagation();
    
    // Double check participation status before calling
    const currentUserStatus = userProgress?.userStatus || 'idle';
    const currentlyParticipating = currentUserStatus === 'active' || currentUserStatus === 'pending_review';
    
    if (currentlyParticipating) {
      onCancelParticipation && onCancelParticipation(challenge);
    } else {
      onParticipate && onParticipate(challenge);
    }
  };

  // Handle admin actions
  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit && onEdit(challenge);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce défi ?')) {
      onDelete && onDelete(challenge);
    }
  };

  // Get display participants (max 5 avatars)
  const displayParticipants = participants.slice(0, 5);
  const extraCount = participants.length > 5 ? participants.length - 5 : 0;

  return (
    <div 
      className={`challenge-card-modern ${difficulty} ${isCancelled ? 'cancelled' : ''} ${isAdmin ? 'has-admin-controls' : ''}`}
      onClick={() => !isCancelled && onClick && onClick(challenge)}
    >
      {/* Admin Controls - Top Right */}
      {isAdmin && (
        <div className="admin-controls">
          <button className="admin-btn edit" onClick={handleEdit} title="Modifier le défi">
            <Edit size={16} />
          </button>
          <button className="admin-btn delete" onClick={handleDelete} title="Supprimer le défi">
            <Trash2 size={16} />
          </button>
        </div>
      )}

      {/* Cancelled Badge */}
      {isCancelled && (
        <div className="cancelled-badge">
          <X size={14} />
          <span>Participation annulée</span>
        </div>
      )}

      {/* Difficulty Badge - Top Left */}
      <div className="difficulty-badge" style={{ background: diffConfig.bg, color: diffConfig.color }}>
        {difficulty === 'easy' ? '✓' : difficulty === 'medium' ? '●' : '▲'}
        <span>{diffConfig.label}</span>
      </div>

      {/* Header Section */}
      <div className="card-header">
        <div className="icon-container">
          <span className="sport-icon"><AppIcon name={displayIcon} size={20} strokeWidth={2} /></span>
        </div>
        <div className="xp-badge">
          <Star size={14} />
          <span>+{rewardXP} XP</span>
        </div>
      </div>

      {/* Title Section */}
      <div className="card-title">
        <h3>{title}</h3>
        <p className="description">{description}</p>
      </div>

      {/* Info Icons Row */}
      <div className="info-row">
        <div className="info-item">
          <Clock size={16} />
          <span>{formatRemaining(endDate)}</span>
        </div>
        <div className="info-item">
          <Clock size={16} />
          <span>{durationLabel}</span>
        </div>
      </div>

      {/* Participants Section - Show for both Admin and Users */}
      <div className="participants-section">
        <div className="participants-header">
          <Users size={16} />
          <span className="participants-count">
            {loadingParticipants ? '...' : `${participants.length || 0} participant${participants.length !== 1 ? 's' : ''}`}
          </span>
        </div>
        {displayParticipants.length > 0 ? (
          <div className="participants-avatars">
            {displayParticipants.map((participant, index) => (
              <div 
                key={participant._id || index} 
                className="participant-avatar"
                title={participant.displayName}
              >
                {participant.photoURL ? (
                  <img src={participant.photoURL} alt={participant.displayName} />
                ) : (
                  <User size={14} />
                )}
              </div>
            ))}
            {extraCount > 0 && (
              <div className="participant-avatar extra">
                +{extraCount}
              </div>
            )}
          </div>
        ) : (
          <div className="no-participants">
            <span>Aucun participant</span>
          </div>
        )}
      </div>

      {/* Progress Section - Only show for non-admin (users can see their progress) */}
      {!isAdmin && (
        <div className="progress-section">
          <div className="progress-header">
            <span className="progress-label">Progression</span>
            <span className="progress-value" style={{ color: isComplete ? '#10b981' : '#3b82f6' }}>
              {currentValue} / {targetValue} {metricType} ({progress}%)
            </span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ 
                width: `${Math.min(progress, 100)}%`,
                background: isComplete 
                  ? 'linear-gradient(90deg, #10b981, #059669)' 
                  : 'linear-gradient(90deg, #3b82f6, #6366f1)'
              }}
            />
          </div>
        </div>
      )}

      {/* Admin View - Show stats instead of progress */}
      {isAdmin && (
        <div className="admin-stats-section">
          <div className="admin-stat">
            <span className="stat-label">Objectif</span>
            <span className="stat-value">{targetValue} {metricType}</span>
          </div>
          <div className="admin-stat">
            <span className="stat-label">Statut</span>
            <span className="stat-value">{challenge?.isActive ? 'Actif' : 'Inactif'}</span>
          </div>
        </div>
      )}

      {/* Action Button - Hide for Admin */}
      {!isAdmin && (
        <div className="card-actions">
          {isParticipating ? (
            <button 
              className="participate-btn cancel"
              onClick={handleParticipate}
            >
              <XCircle size={18} />
              <span>Annuler participation</span>
            </button>
          ) : isCompleted ? (
            <button className="participate-btn completed" disabled>
              <CheckCircle size={18} />
              <span>Terminé</span>
            </button>
          ) : isCancelled ? (
            <button className="participate-btn cancelled-disabled" disabled>
              <XCircle size={18} />
              <span>Défi annulé - Vous ne pouvez pas participer</span>
            </button>
          ) : (
            <button 
              className="participate-btn"
              onClick={handleParticipate}
            >
              <PlayCircle size={18} />
              <span>Participer</span>
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      )}

      {/* Admin Notice - Instead of participation buttons */}
      {isAdmin && (
        <div className="admin-notice">
          <span>Mode Administration - Surveillance uniquement</span>
        </div>
      )}
    </div>
  );
}

// ==================== CHALLENGE GRID ====================
export function ChallengeCardGrid({ challenges = [], onSelectChallenge, ...props }) {
  // Demo data if no challenges provided
  const demoChallenges = [
    {
      _id: '1',
      title: 'Win 3 tennis matches',
      description: 'Complete 3 matches to earn XP',
      rewardXP: 50,
      targetValue: 3,
      metricType: 'matchs',
      endDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
      difficulty: 'easy',
      icon: '🎾',
      userProgress: { progress: 33, currentValue: 1 }
    },
    {
      _id: '2',
      title: 'Win 10 tennis matches',
      description: 'Become a tennis champion',
      rewardXP: 200,
      targetValue: 10,
      metricType: 'matchs',
      endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      difficulty: 'hard',
      icon: '🎾',
      userProgress: { progress: 0, currentValue: 0 }
    },
    {
      _id: '3',
      title: 'Practice 5 hours',
      description: 'Improve your skills',
      rewardXP: 100,
      targetValue: 5,
      metricType: 'heures',
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      difficulty: 'medium',
      icon: '⏱️',
      userProgress: { progress: 60, currentValue: 3 }
    },
    {
      _id: '4',
      title: 'Join a tournament',
      description: 'Participate in a local tournament',
      rewardXP: 150,
      targetValue: 1,
      metricType: 'tournoi',
      endDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
      difficulty: 'medium',
      icon: '🏆',
      userProgress: { progress: 100, currentValue: 1, userStatus: 'completed' }
    }
  ];

  const displayChallenges = challenges.length > 0 ? challenges : demoChallenges;

  return (
    <div className="challenges-grid-modern">
      {displayChallenges.map((challenge, index) => (
        <ChallengeCard
          key={challenge._id || index}
          challenge={challenge}
          onClick={() => onSelectChallenge && onSelectChallenge(challenge)}
          {...props}
        />
      ))}
    </div>
  );
}

